import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { update_chatroom_status_thunk } from "../../redux/chatroom/chatroomThunk";
import { FaCheckCircle, FaSearch, FaEllipsisV } from "react-icons/fa";
import {
  Video,
  FileText,
  AlertTriangle,
  Clock,
  X,
  Settings,
} from "lucide-react";
import socketService from "../../services/socket";
import { updateChatRoomMediaLink } from "../../redux/chatroom/chatroomSlice";
import { USER_AU_USER } from "../../common/key";

const ConfirmationModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-gray-700 shadow-xl">
        <h3 className="text-xl font-semibold text-white mb-4">
          Confirm Success
        </h3>
        <p className="text-gray-300 mb-6">
          Are you sure your task is completed? This will mark the chat as
          successful.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
          >
            No, Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-md transition-colors"
          >
            Yes, Complete
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatHeader = ({
  activeChatDetails,
  messages,
  isMobile,
  onToggleSidebar,
}) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMediaLinkModal, setShowMediaLinkModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [mediaType, setMediaType] = useState(null); // 'video' or 'document' or 'both'
  const statusUpdating = useSelector((state) => state.chatroom.statusUpdating);

  // Helper function to check if a setting is enabled, supporting both formats
  const isSettingEnabled = (settingKey) => {
    if (!activeChatDetails?.settings) {
      return false;
    }

    const settings = activeChatDetails.settings;
    const setting = settings[settingKey];

    // Log the specific setting being checked (for debugging)

    // If setting doesn't exist or is null/undefined
    if (setting === undefined || setting === null) {
      return false;
    }

    // For video_required and document_required specific handling
    if (settingKey === "video_required" || settingKey === "document_required") {
      // If it's a simple boolean
      if (typeof setting === "boolean") {
        return setting === true;
      }

      // If it's a truthy value like "true" or 1
      if (setting === "true" || setting === 1) {
        return true;
      }

      // If it's an object with enabled property
      if (typeof setting === "object" && setting !== null) {
        if ("enabled" in setting) {
          return setting.enabled === true;
        }

        // If it doesn't have an enabled property but has other truthy values
        return Object.values(setting).some(
          (v) => v === true || v === "true" || v === 1
        );
      }

      // If it's any other truthy value
      return !!setting;
    }

    // Check for new format (object with enabled property)
    if (typeof setting === "object" && setting !== null) {
      // Direct check for enabled property first
      if ("enabled" in setting) {
        return setting.enabled === true;
      }

      // For check_in_interval stored as object with numeric options
      if (
        settingKey === "check_in_interval" &&
        setting.options &&
        setting.options.interval
      ) {
        return setting.options.interval > 0;
      }

      // Generic check: if object has truthy values
      return Object.values(setting).some((v) => v);
    }

    // For boolean values (direct)
    if (typeof setting === "boolean") {
      return setting === true;
    }

    // For numeric values like check_in_interval
    if (
      typeof setting === "number" &&
      (settingKey === "check_in_interval" ||
        settingKey === "days" ||
        settingKey === "priority")
    ) {
      return setting > 0;
    }

    // Default case
    return !!setting; // Convert to boolean
  };

  // Helper function to get a numeric setting value, supporting both formats
  const getNumericValue = (settingKey, defaultValue = 0) => {
    if (!activeChatDetails?.settings) {
      return defaultValue;
    }

    const settings = activeChatDetails.settings;
    const setting = settings[settingKey];

    // If setting doesn't exist or is null/undefined
    if (setting === undefined || setting === null) {
      return defaultValue;
    }

    // For direct numeric values
    if (typeof setting === "number") {
      return setting;
    }

    // For structured format (object with options)
    if (typeof setting === "object") {
      // If it has options property with numeric values
      if (setting.options) {
        // Check all possible property names
        const value =
          setting.options.level ||
          setting.options.count ||
          setting.options.interval ||
          setting.options.value;

        if (typeof value === "number") {
          return value;
        }
      }

      // For check_in_interval specifically
      if (settingKey === "check_in_interval" && "enabled" in setting) {
        // If disabled, return 0
        if (setting.enabled === false) {
          return 0;
        }
      }
    }

    // Boolean true values for some settings might map to 1
    if (
      setting === true &&
      (settingKey === "check_in_interval" || settingKey === "days")
    ) {
      return 1;
    }

    return defaultValue;
  };

  // Function to render settings tags
  const renderSettingsTags = () => {
    if (!activeChatDetails?.settings) {
      return null;
    }

    const tags = [];
    const settings = activeChatDetails.settings;

    // Enhanced debugging for settings

    // Add tags based on settings
    if (isSettingEnabled("urgent")) {
      tags.push(
        <div
          key="urgent"
          className="flex items-center px-2 py-1 bg-red-600 text-white rounded-md mr-1"
        >
          <AlertTriangle size={14} className="mr-1" />
          <span className="text-xs">Urgent</span>
        </div>
      );
    }

    if (isSettingEnabled("disable_speak")) {
      tags.push(
        <div
          key="disable-speak"
          className="flex items-center px-2 py-1 bg-gray-600 text-white rounded-md mr-1"
        >
          <span className="text-xs">Disable Speak</span>
        </div>
      );
    }

    // Check for video_required in various formats
    const videoRequired = isSettingEnabled("video_required");
    if (videoRequired) {
      // Direct check if Video is required based on the settings
      tags.push(
        <div
          key="video"
          className="flex items-center px-2 py-1 bg-blue-600 text-white rounded-md mr-1"
        >
          <Video size={14} className="mr-1" />
          <span className="text-xs">Video</span>
        </div>
      );
    }

    // Check for document_required in various formats
    const docRequired = isSettingEnabled("document_required");
    if (docRequired) {
      tags.push(
        <div
          key="doc"
          className="flex items-center px-2 py-1 bg-emerald-600 text-white rounded-md mr-1"
        >
          <FileText size={14} className="mr-1" />
          <span className="text-xs">Doc</span>
        </div>
      );
    }

    if (isSettingEnabled("continuous")) {
      tags.push(
        <div
          key="continuous"
          className="flex items-center px-2 py-1 bg-indigo-600 text-white rounded-md mr-1"
        >
          <span className="text-xs">Continuous</span>
        </div>
      );
    }

    if (isSettingEnabled("approval_required")) {
      tags.push(
        <div
          key="approval"
          className="flex items-center px-2 py-1 bg-yellow-600 text-white rounded-md mr-1"
        >
          <span className="text-xs">Approval</span>
        </div>
      );
    }

    const priorityValue = getNumericValue("priority", 0);
    if (priorityValue > 1) {
      tags.push(
        <div
          key="priority"
          className="flex items-center px-2 py-1 bg-amber-600 text-white rounded-md mr-1"
        >
          <span className="text-xs">P{priorityValue}</span>
        </div>
      );
    }

    const daysValue = getNumericValue("days", 0);
    if (daysValue > 1) {
      tags.push(
        <div
          key="days"
          className="flex items-center px-2 py-1 bg-purple-600 text-white rounded-md mr-1"
        >
          <Clock size={14} className="mr-1" />
          <span className="text-xs">{daysValue}d</span>
        </div>
      );
    }

    const checkInInterval = getNumericValue("check_in_interval", 0);
    if (checkInInterval > 0) {
      tags.push(
        <div
          key="check-in-interval"
          className="flex items-center px-2 py-1 bg-pink-600 text-white rounded-md mr-1"
        >
          <Clock size={14} className="mr-1" />
          <span className="text-xs">{checkInInterval}d Check-in</span>
        </div>
      );
    } else if (isSettingEnabled("check_in_interval")) {
      // Fallback if we detect the setting is enabled but couldn't extract numeric value
      tags.push(
        <div
          key="check-in-interval-fallback"
          className="flex items-center px-2 py-1 bg-pink-600 text-white rounded-md mr-1"
        >
          <Clock size={14} className="mr-1" />
          <span className="text-xs">Check-in</span>
        </div>
      );
    }

    return tags.length > 0 ? (
      <div className="flex items-center mr-2 overflow-x-auto hide-scrollbar">
        {tags}
      </div>
    ) : null;
  };

  const handleMarkAsSuccess = async () => {
    if (!activeChatDetails?._id) return;

    const settings = activeChatDetails?.settings || {};

    // Force detection of Video and Doc tags from the UI
    // This is a fallback in case the settings object structure is unexpected
    const hasVideoTag = document
      .querySelector(".bg-blue-600")
      ?.textContent?.includes("Video");
    const hasDocTag = document
      .querySelector(".bg-emerald-600")
      ?.textContent?.includes("Doc");

    // Use the isSettingEnabled function to consistently check if settings are enabled
    const hasVideoReq = isSettingEnabled("video_required") || hasVideoTag;
    const hasDocReq = isSettingEnabled("document_required") || hasDocTag;

    // Close the confirmation modal first
    setIsModalOpen(false);

    if (hasVideoReq && hasDocReq) {
      setMediaType("both");
      setShowMediaLinkModal(true);
    } else if (hasVideoReq) {
      setMediaType("video");
      setShowMediaLinkModal(true);
    } else if (hasDocReq) {
      setMediaType("document");
      setShowMediaLinkModal(true);
    } else {
      // No media required, just mark as success
      dispatch(
        update_chatroom_status_thunk({
          roomId: activeChatDetails._id,
          status: "success",
        })
      );
    }
  };

  const isSuccess = activeChatDetails?.status === "success";

  // Debug useEffect to track settings changes
  useEffect(() => {
    if (activeChatDetails?.settings) {
      const settings = activeChatDetails.settings;
    }
  }, [activeChatDetails?.settings]);

  // Additional effect to check for UI tags and pre-set media type when component mounts
  useEffect(() => {
    if (activeChatDetails && !isSuccess) {
      // Check for UI tags
      const hasVideoTag = document
        .querySelector(".bg-blue-600")
        ?.textContent?.includes("Video");
      const hasDocTag = document
        .querySelector(".bg-emerald-600")
        ?.textContent?.includes("Doc");

      if (hasVideoTag && hasDocTag) {
        setMediaType("both");
      } else if (hasVideoTag) {
        setMediaType("video");
      } else if (hasDocTag) {
        setMediaType("document");
      }
    }
  }, [activeChatDetails, isSuccess]);

  // Add this function to count active settings
  const getActiveSettingsCount = () => {
    if (!activeChatDetails?.settings) return 0;

    const settings = activeChatDetails.settings;
    let count = 0;

    // Helper function to check if a setting is enabled
    const isSettingEnabled = (settingKey) => {
      const setting = settings[settingKey];
      if (setting && typeof setting === "object" && "enabled" in setting) {
        return setting.enabled === true;
      }
      return setting === true;
    };

    // Count enabled boolean settings
    if (isSettingEnabled("disable_speak")) count++;
    if (isSettingEnabled("video_required")) count++;
    if (isSettingEnabled("document_required")) count++;
    if (isSettingEnabled("continuous")) count++;
    if (isSettingEnabled("urgent")) count++;
    if (isSettingEnabled("approval_required")) count++;

    // Count numeric settings that have values
    const getNumericValue = (settingKey, defaultValue = 0) => {
      const setting = settings[settingKey];
      if (setting && typeof setting === "object") {
        if (setting.options) {
          return (
            setting.options.level ||
            setting.options.count ||
            setting.options.interval ||
            setting.options.value ||
            defaultValue
          );
        }
        return defaultValue;
      }
      return typeof setting === "number" ? setting : defaultValue;
    };

    if (getNumericValue("priority", 0) > 0) count++;
    if (getNumericValue("days", 0) > 0) count++;
    if (getNumericValue("check_in_interval", 0) > 0) count++;

    return count;
  };

  return (
    <>
      <div className="px-4 py-3 border-b border-gray-800 bg-black bg-opacity-40 shadow-md flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* {isMobile && (
            <button
              onClick={onToggleSidebar}
              className="text-gray-400 hover:text-purple-400 mr-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          )} */}
          <div className="h-10 w-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-semibold shadow-md">
            {activeChatDetails?.name?.[0]}
          </div>
          <div>
            <h2 className="font-bold text-white text-lg">
              {activeChatDetails?.name}
            </h2>
            {/* <div className="flex items-center space-x-2">
              <p className="text-xs text-gray-400">
                {messages.length > 4
                  ? "4+ messages"
                  : `${messages.length} message${
                      messages.length !== 1 ? "s" : ""
                    }`}
              </p>
            </div> */}
          </div>
        </div>
        <div className="flex space-x-2">
          {/* Settings button */}
          <button
            onClick={() => setShowSettingsModal(true)}
            className="flex items-center px-3 py-1 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
            title="View Settings"
          >
            <Settings size={16} className="mr-1.5" />
            <span className="text-sm font-medium">Settings</span>
            {activeChatDetails?.settings && (
              <span className="bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ml-1">
                {getActiveSettingsCount()}
              </span>
            )}
          </button>

          {isSuccess ? (
            <div className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md">
              <FaCheckCircle className="mr-1.5" size={16} />
              <span className="text-sm font-medium">Completed</span>
            </div>
          ) : (
            <button
              onClick={() => {
                // Check for UI tags before opening confirmation modal
                const hasVideoTag = document
                  .querySelector(".bg-blue-600")
                  ?.textContent?.includes("Video");
                const hasDocTag = document
                  .querySelector(".bg-emerald-600")
                  ?.textContent?.includes("Doc");

                if (hasVideoTag || hasDocTag) {
                  if (hasVideoTag && hasDocTag) {
                    setMediaType("both");
                  } else if (hasVideoTag) {
                    setMediaType("video");
                  } else {
                    setMediaType("document");
                  }
                  setShowMediaLinkModal(true);
                } else {
                  // No tags, show regular confirmation
                  setIsModalOpen(true);
                }
              }}
              disabled={statusUpdating}
              className="flex items-center px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {/* <FaCheckCircle className="mr-1.5" size={16} /> */}
              <span className="text-sm font-medium">Mark as Success</span>
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleMarkAsSuccess}
      />

      {/* Media Link Modal */}
      {showMediaLinkModal && activeChatDetails && (
        <MediaLinkModal
          chatRoom={activeChatDetails}
          mediaType={mediaType}
          onClose={() => setShowMediaLinkModal(false)}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && activeChatDetails && (
        <SettingsModal
          isOpen={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
          activeChatDetails={activeChatDetails}
          renderSettingsTags={renderSettingsTags}
        />
      )}
    </>
  );
};

// MediaLinkModal Component
const MediaLinkModal = ({ chatRoom, mediaType, onClose }) => {
  const dispatch = useDispatch();
  const [videoLink, setVideoLink] = useState("");
  const [documentLink, setDocumentLink] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);

  // Get the user from localStorage
  useEffect(() => {
    const userDataString = localStorage.getItem(USER_AU_USER);
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setUser(userData);
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    }
  }, []);

  const handleSubmit = async () => {
    if (isSubmitting || !user) return; // Prevent double submission

    try {
      setIsSubmitting(true);

      const isBothRequired = mediaType === "both";
      const videoRequired = mediaType === "video" || isBothRequired;
      const docRequired = mediaType === "document" || isBothRequired;

      // Validate that all required fields have values
      if ((videoRequired && !videoLink) || (docRequired && !documentLink)) {
        alert("Please fill in all required fields");
        setIsSubmitting(false);
        return;
      }

      // Save the links to the server and notify other users via socket
      if (chatRoom._id) {
        // Handle video link if provided
        if (videoLink && videoRequired) {
          socketService.getSocket().emit("submit_media_link", {
            roomId: chatRoom._id,
            userId: user._id,
            mediaType: "video",
            link: videoLink,
          });

          // Update local state with the new media link
          dispatch(
            updateChatRoomMediaLink({
              roomId: chatRoom._id,
              mediaType: "video",
              link: videoLink,
            })
          );
        }

        // Handle document link if provided
        if (documentLink && docRequired) {
          socketService.getSocket().emit("submit_media_link", {
            roomId: chatRoom._id,
            userId: user._id,
            mediaType: "document",
            link: documentLink,
          });

          // Update local state with the new media link
          dispatch(
            updateChatRoomMediaLink({
              roomId: chatRoom._id,
              mediaType: "document",
              link: documentLink,
            })
          );
        }

        // Update the chat room status to success after media link submission
        await dispatch(
          update_chatroom_status_thunk({
            roomId: chatRoom._id,
            status: "success",
          })
        ).unwrap();

        // Close the modal
        onClose();
      }
    } catch (error) {
      console.error("Error submitting media links:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl p-6 w-96 border border-purple-700 shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-purple-400 flex items-center">
            {mediaType === "both" ? (
              <>
                <FileText size={20} className="mr-2" />
                Add Required Media
              </>
            ) : mediaType === "video" ? (
              <>
                <Video size={20} className="mr-2" />
                Add Video Link
              </>
            ) : (
              <>
                <FileText size={20} className="mr-2" />
                Add Document Link
              </>
            )}
          </h2>
          <div
            onClick={onClose}
            className="cursor-pointer text-gray-400 hover:text-pink-400 transition-colors"
          >
            <X size={20} />
          </div>
        </div>

        {(mediaType === "video" || mediaType === "both") && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
              <Video size={16} className="mr-2" />
              Video URL
            </label>
            <input
              type="text"
              placeholder="Enter video URL..."
              value={videoLink}
              onChange={(e) => setVideoLink(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}

        {(mediaType === "document" || mediaType === "both") && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center">
              <FileText size={16} className="mr-2" />
              Document URL
            </label>
            <input
              type="text"
              placeholder="Enter document URL..."
              value={documentLink}
              onChange={(e) => setDocumentLink(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              (mediaType === "video" && !videoLink) ||
              (mediaType === "document" && !documentLink) ||
              (mediaType === "both" && (!videoLink || !documentLink))
            }
            className={`px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-500 hover:to-pink-500 shadow-lg hover:shadow-purple-500/20 transition-all ${
              isSubmitting ||
              (mediaType === "video" && !videoLink) ||
              (mediaType === "document" && !documentLink) ||
              (mediaType === "both" && (!videoLink || !documentLink))
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

// Settings Modal Component
const SettingsModal = ({
  isOpen,
  onClose,
  activeChatDetails,
  renderSettingsTags,
}) => {
  if (!isOpen || !activeChatDetails?.settings) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-xl p-6 w-full max-w-md border border-purple-700 shadow-xl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-purple-400 flex items-center">
            Chat Settings
          </h2>
          <div
            onClick={onClose}
            className="cursor-pointer text-gray-400 hover:text-pink-400 transition-colors"
          >
            <X size={20} />
          </div>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-3">
          {activeChatDetails && (
            <div className="flex flex-wrap gap-2 col-span-2">
              {renderSettingsTags()}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
